import { useRef, useState } from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { useScroll, useMotionValueEvent } from 'framer-motion'
import { craftsmanship } from '@/content/copy'
import { productImagesFor } from '@/content/products'
import { featureIcons } from '@/lib/icons'
import { colors, fonts, layout } from '@/theme'
import { Container } from '@/components/ui/Container'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { Media } from '@/components/ui/Media'

/**
 * Materials & craftsmanship — a scroll-pinned sequence. On desktop the whole
 * section locks to the viewport (sticky) while scrolling scrubs through the four
 * build-points: the left image cross-fades and each point ignites on its beat.
 * After the last beat the pin releases and the page continues to the next section.
 * On mobile it degrades to a normal stacked layout (no scroll-jack).
 */
export function Craftsmanship() {
  const points = craftsmanship.points
  const gallery = productImagesFor(craftsmanship.imageSku)
  // A few distinct shots to cross-fade through as the sequence advances.
  const shots = [gallery[5], gallery[0], gallery[2], gallery[4]]
    .filter(Boolean)
    .slice(0, points.length) as typeof gallery
  const total = points.length

  const sectionRef = useRef<HTMLDivElement>(null)
  // Tall track → progress 0 (section top at viewport top) to 1 (section bottom at viewport bottom).
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })
  const [active, setActive] = useState(0)
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setActive(Math.max(0, Math.min(total - 1, Math.floor(v * total))))
  })

  const shotIndex = Math.min(active, Math.max(0, shots.length - 1))

  return (
    // Outer = scroll track: one viewport of scroll per build-point (desktop only).
    <Box component="section" ref={sectionRef} sx={{ position: 'relative', height: { md: `${total * 100}vh` } }}>
      {/* Inner = the pinned frame that stays locked while the sequence scrubs. */}
      <Box
        sx={{
          position: { md: 'sticky' },
          top: 0,
          height: { md: '100dvh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          // Clear the fixed header so the locked content sits centred in the
          // visible area below it — content is sized to fit, so nothing crops.
          pt: { xs: 10, md: `${layout.headerHeight + 24}px` },
          pb: { xs: 10, md: 5 },
        }}
      >
        <Container sx={{ width: '100%' }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            {/* Locked sequence image */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: `${layout.radius.card}px`,
                  overflow: 'hidden',
                  border: `1px solid ${colors.border}`,
                  aspectRatio: '4 / 5',
                  bgcolor: colors.surfaceAlt,
                }}
              >
                {shots.length > 0 ? (
                  shots.map((img, i) => (
                    <Box
                      key={img.src}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: i === shotIndex ? 1 : 0,
                        transition: 'opacity 700ms ease',
                      }}
                    >
                      <Media src={img.src} srcSm={img.srcSm} alt="True Forge welding glove — build stage" width={img.width} height={img.height} ratio={4 / 5} sx={{ height: '100%' }} />
                    </Box>
                  ))
                ) : (
                  <Box sx={{ position: 'absolute', inset: 0, bgcolor: colors.surfaceAlt }} />
                )}

                {/* Stage counter + progress ticks */}
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: { xs: 2.5, md: 3 }, background: 'linear-gradient(0deg, rgba(13,13,13,0.85), transparent)' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 26, color: colors.gold, lineHeight: 1 }}>
                      {String(active + 1).padStart(2, '0')}
                      <Box component="span" sx={{ color: colors.grey, fontSize: 18 }}> / {String(total).padStart(2, '0')}</Box>
                    </Typography>
                    <Stack direction="row" spacing={0.75}>
                      {points.map((_, i) => (
                        <Box key={i} sx={{ width: i === active ? 20 : 8, height: 3, borderRadius: 2, bgcolor: i === active ? colors.gold : colors.borderStrong, transition: 'all 350ms ease' }} />
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            </Grid>

            {/* Content */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Eyebrow>{craftsmanship.eyebrow}</Eyebrow>
              <Typography variant="h2" component="h2" sx={{ maxWidth: 620 }}>
                {craftsmanship.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3, maxWidth: 620 }}>
                {craftsmanship.body}
              </Typography>

              <Stack spacing={0} sx={{ mt: { xs: 3, md: 5 } }}>
                {points.map((p, i) => {
                  const Icon = featureIcons[p.icon]
                  const on = i === active
                  return (
                    <Box
                      key={p.title}
                      sx={{
                        display: 'flex',
                        gap: { xs: 2, md: 3 },
                        py: { xs: 2.5, md: 2 },
                        borderTop: i === 0 ? 'none' : `1px solid ${colors.border}`,
                        // Mobile shows all points lit (no scrub); desktop dims the inactive ones.
                        opacity: { xs: 1, md: on ? 1 : 0.5 },
                        transition: 'opacity 350ms ease',
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 46,
                          height: 46,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          color: on ? colors.bg : colors.gold,
                          bgcolor: on ? colors.gold : 'transparent',
                          border: `1px solid ${on ? colors.gold : colors.borderStrong}`,
                          boxShadow: on ? `0 0 22px -4px ${colors.gold}` : 'none',
                          transition: 'all 350ms ease',
                        }}
                      >
                        {Icon && <Icon size={20} />}
                      </Box>
                      <Box>
                        <Typography variant="h6" component="h3" sx={{ color: colors.white }}>
                          {p.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                          {p.text}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}
